/**
 * OrderList Component
 *
 * Displays a paginated list of customer orders fetched from the API.
 * Supports search, filtering by organization, and navigation to order details.
 *
 * Uses:
 * - useCustomerOrders hook for data fetching
 * - useCustomerOrganizations hook for multi-tenant organization filtering
 * - useAuthConfig hook for authentication
 */

import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Filter, Eye, XCircle, Building2, RefreshCw, Package } from 'lucide-react';
import {
  useCustomerOrders,
  useCustomerOrganizations,
  type CustomerOrderStatus,
} from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

interface OrderListProps {
  onSelectOrder: (orderId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge Component
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: CustomerOrderStatus }> = ({ status }) => {
  const styles: Record<CustomerOrderStatus, string> = {
    Draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    Placed: 'bg-blue-50 text-blue-700 border-blue-200',
    'In Transit': 'bg-amber-50 text-amber-700 border-amber-200',
    Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${styles[status]}`}>
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

const OrderListSkeleton: React.FC = () => (
  <div data-testid="order-list-loading" className="flex flex-col h-full overflow-hidden bg-white">
    <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
      <div className="h-4 w-32 bg-zinc-200 rounded animate-pulse" />
      <div className="flex items-center gap-2">
        <div className="h-8 w-48 bg-zinc-200 rounded animate-pulse" />
        <div className="h-8 w-20 bg-zinc-200 rounded animate-pulse" />
      </div>
    </div>
    <div className="flex-1 p-6 space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 p-4 border border-zinc-200 rounded-lg">
          <div className="h-4 w-28 bg-zinc-200 rounded animate-pulse" />
          <div className="h-4 w-40 bg-zinc-200 rounded animate-pulse" />
          <div className="h-4 w-20 bg-zinc-200 rounded animate-pulse" />
          <div className="flex-1" />
          <div className="h-4 w-32 bg-zinc-200 rounded animate-pulse" />
          <div className="h-4 w-20 bg-zinc-200 rounded animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Error State
// ─────────────────────────────────────────────────────────────────────────────

const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center h-full bg-white p-8">
    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
      <XCircle className="w-8 h-8 text-red-500" />
    </div>
    <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to Load Orders</h3>
    <p className="text-sm text-zinc-500 mb-4 text-center max-w-sm">
      We couldn't load your orders. Please check your connection and try again.
    </p>
    <button
      onClick={onRetry}
      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
    >
      <RefreshCw size={16} />
      Retry
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full bg-white p-8">
    <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mb-4">
      <Package className="w-8 h-8 text-zinc-400" />
    </div>
    <h3 className="text-lg font-semibold text-zinc-900 mb-2">No Orders Yet</h3>
    <p className="text-sm text-zinc-500 text-center max-w-sm">
      You haven't placed any orders yet. Create your first order to get started.
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main OrderList Component
// ─────────────────────────────────────────────────────────────────────────────

export const OrderList: React.FC<OrderListProps> = ({ onSelectOrder }) => {
  const authConfig = useAuthConfig();

  // State for pagination and filtering
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CustomerOrderStatus | undefined>();
  const [organizationFilter, setOrganizationFilter] = useState<string | undefined>();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch organizations for filtering
  const { data: orgsData } = useCustomerOrganizations({}, { authConfig });
  const organizations = orgsData?.data ?? [];

  // Helper to get organization name by ID
  const getOrgName = (orgId: string): string => {
    const org = organizations.find((o) => o.id === orgId);
    return org?.name || 'Unknown Partner';
  };

  // Fetch orders
  const { data, isLoading, isError, refetch } = useCustomerOrders(
    {
      page,
      per_page: 20,
      q: debouncedSearch || undefined,
      status: statusFilter,
      organization_id: organizationFilter,
    },
    { authConfig }
  );

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      window.addEventListener('click', handleClickOutside);
    }
    return () => window.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  // Handle organization filter change
  const handleOrganizationFilterChange = (value: string) => {
    setOrganizationFilter(value || undefined);
    setPage(1); // Reset to first page on filter change
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter((value as CustomerOrderStatus) || undefined);
    setPage(1); // Reset to first page on filter change
  };

  // Status options for filter
  const statusOptions: CustomerOrderStatus[] = [
    'Draft',
    'Placed',
    'In Transit',
    'Completed',
    'Cancelled',
  ];

  // Loading state
  if (isLoading) {
    return <OrderListSkeleton />;
  }

  // Error state
  if (isError) {
    return <ErrorState onRetry={() => refetch()} />;
  }

  const orders = data?.data ?? [];
  const pagination = data?.pagination;

  // Empty state
  if (orders.length === 0 && !debouncedSearch && !statusFilter && !organizationFilter) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-10">
        <h2 className="text-sm font-semibold text-zinc-900">Recent Orders</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white border border-zinc-200 text-zinc-900 text-xs rounded px-3 py-1.5 w-48 focus:outline-none focus:border-zinc-400 placeholder-zinc-400 transition-all"
            />
          </div>
          {/* Organization Filter Dropdown */}
          <select
            aria-label="Filter by partner"
            value={organizationFilter || ''}
            onChange={(e) => handleOrganizationFilterChange(e.target.value)}
            className="bg-white border border-zinc-200 text-zinc-900 text-xs rounded px-3 py-1.5 focus:outline-none focus:border-zinc-400 transition-all"
          >
            <option value="">All Partners</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-1.5">
            <Filter size={12} className="text-zinc-400" />
            <select
              aria-label="Filter by status"
              value={statusFilter || ''}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="bg-white border border-zinc-200 text-zinc-900 text-xs rounded px-2 py-1.5 focus:outline-none focus:border-zinc-400 transition-all"
            >
              <option value="">All Statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-0 pb-12">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
            <Package className="w-12 h-12 mb-4 text-zinc-300" />
            <p className="text-sm">No orders found matching your search</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm border-collapse">
            <thead className="sticky top-0 bg-white z-10 border-b border-zinc-200 text-xs uppercase text-zinc-500 font-medium shadow-sm">
              <tr>
                <th className="px-6 py-3 w-28 font-semibold">Order ID</th>
                <th className="px-6 py-3 w-40 font-semibold">Partner</th>
                <th className="px-6 py-3 w-28 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Route</th>
                <th className="px-6 py-3 w-40 font-semibold">Recipient</th>
                <th className="px-6 py-3 w-28 text-right font-semibold">Price</th>
                <th className="px-6 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {orders.map((order) => (
                <tr
                  key={order.id}
                  className="group hover:bg-zinc-50 transition-colors cursor-default"
                >
                  <td className="px-6 py-3 font-mono text-zinc-600 text-xs font-medium">
                    #{order.id}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 text-zinc-700 text-xs">
                      <Building2 size={12} className="text-zinc-400" />
                      <span className="truncate max-w-[120px] font-medium">
                        {getOrgName(order.organization_id)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-col gap-1 text-[11px]">
                      <div className="flex items-center gap-2 text-zinc-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-300"></div>
                        <span className="truncate max-w-[150px]">
                          {order.pickup.address.street}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                        <span className="truncate max-w-[150px] font-medium">
                          {order.dropoff.address.street}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-col">
                      <span className="text-zinc-700 text-xs font-medium">
                        {order.dropoff.contact.name}
                      </span>
                      <span className="text-zinc-500 text-[10px]">
                        {order.dropoff.contact.phone}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right text-zinc-600 text-xs font-mono font-medium">
                    ${order.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-3 text-right relative">
                    <div className="relative">
                      <button
                        aria-label="More options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === order.id ? null : order.id);
                        }}
                        className={`text-zinc-400 hover:text-zinc-700 transition-colors p-1 rounded hover:bg-zinc-100 ${
                          openMenuId === order.id
                            ? 'opacity-100 text-zinc-700 bg-zinc-100'
                            : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {openMenuId === order.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 py-1 flex flex-col">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectOrder(order.id);
                              setOpenMenuId(null);
                            }}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left w-full"
                          >
                            <Eye size={12} className="text-zinc-400" /> View Details
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="border-t border-zinc-200 px-6 py-3 flex items-center justify-between bg-white">
          <p className="text-xs text-zinc-500">
            Showing {(pagination.page - 1) * pagination.per_page + 1} to{' '}
            {Math.min(pagination.page * pagination.per_page, pagination.total_items)} of{' '}
            {pagination.total_items} orders
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.has_prev}
              className="px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-zinc-600">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.has_next}
              className="px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
